import BaseService from "../base-service";
import NetworkUrls from "../network-urls";

class ShoppingCartService extends BaseService {
    uuid = '';
    token = '';

    setUuid(uuid) {
        this.uuid = uuid;
    }

    setAuth(accessToken) {
        this.token = accessToken;
    }

    getHeaders(append = {}) {
        const o = {};
        if (this.uuid && this.uuid !== '') {
            o['X-SC'] = this.uuid;
        }
        if (this.token && this.token !== '') {
            o['Authorization'] = this.token;
        }
        return Object.assign(append, o);
    }

    async fetchBooking(id, accessToken, lang = 'FR') {
        const query = `
        fragment prices on RoomPriceEdge {
            hostingPrice,
            additionalPrice,
             optionsAdditionalPrice,
             optionsBasePrice,
             extraAdditionalPrice,
            touristTax,
            totalPrice,
            from {
                id,
                label,
                verification_modal,
                price_options {
                    id,
                    text (lang: $lang) {
                        checkbox,
                        use_modal,
                        modal_title,
                        modal_content
                    }
                }
                text (lang: $lang) {
                    color,
                    left_label,
                    title,
                    sub_title,
                    description
                }
            }
        }
        fragment roomDetails on Room {
            id,
            color,
            main_picture {
                absolute_path
            },
            pictures (limit: 3, onlyMain: false) {
                absolute_path
            }
            equipments {
                icon_dark: icon (color: "#444444")
            },
            text (lang: $lang) {
                name,
                short_name,
                description,
                short_desc
            },
            provider
        }
        fragment extraDetail on Extra {
            id,
            price,
            picture,
            is_next_day,
            time_to_lock,
            type,
            discount {
                amount,
                type,
                is_available
            },
            text (lang: $lang) {
                name,
                description
            }
        }
        query ($lang: Lang!, $id: CID!) {
            ShoppingCart {
                booking (id: $id) {
                    start,
                    end,
                    price_options {
                        id,
                        text (lang: $lang) {
                            checkbox
                        }
                    },
                    room_price {
                        id,
                        label,
                        verification_modal,
                        price_options {
                            id,
                            text (lang: $lang) {
                                checkbox,
                                use_modal,
                                modal_title,
                                modal_content
                            }
                        }
                        text (lang: $lang) {
                            title,
                            color,
                            sub_title,
                            description
                        }
                    },
                    booking_rooms {
                        price_plans {
                            date,
                            input_prices {
                            nbGuest,
                            edges {
                                ...prices
                                altPrice {
                                ...prices
                                }
                            }
                            }
                        }
                        room {
                            ...roomDetails
                        },
                        extras {
                            is_option,
                            extra {
                                ...extraDetail,
                            },
                            dates {
                                date,
                                count
                            },
                        }
                    }
                }
            }
        }`;
        const config = {
            url: NetworkUrls.graphql,
            method: 'POST',
            headers: this.getHeaders({
                'X-SC': this.uuid,
                Authorization: accessToken
            }),
            data: {
                query,
                variables: {id, lang}
            }
        };
        return this._exec(config);
    }

    async retrieveV2(uuid, token, lang) {
        this.uuid = uuid;
        const config = {
            url: NetworkUrls.graphql,
            method: 'POST',
            headers: this.getHeaders({
                'X-SC': this.uuid,
                Authorization: token
            }),
            data: {
                query: `
                    fragment prices on RoomPriceEdge {
                        hostingPrice,
                        additionalPrice,
                        optionsAdditionalPrice,
                        optionsBasePrice,
                        extraAdditionalPrice,
                        touristTax,
                        totalPrice,
                        from {
                            id,
                            label,
                            verification_modal,
                            price_options {
                                id,
                                text (lang: $lang) {
                                    checkbox,
                                    use_modal,
                                    modal_title,
                                    modal_content
                                }
                            }
                            text (lang: $lang) {
                                color,
                                left_label,
                                title,
                                sub_title,
                                description
                            }
                        }
                    }
                    fragment roomDetails on Room {
                        id,
                        color,
                        main_picture {
                            absolute_path
                        },
                        pictures (limit: 3, onlyMain: false) {
                            absolute_path
                        }
                        equipments {
                            icon_dark
                        },
                        text (lang: $lang) {
                            name,
                            short_name,
                            description,
                            short_desc
                        }
                    }
                    fragment extraDetail on Extra {
                        id,
                        price,
                        picture,
                        is_next_day,
                        time_to_lock,
                        type,
                        discount {
                            amount,
                            type,
                            is_available
                        },
                        text (lang: $lang) {
                            name,
                            description
                        }
                    }
                    query ($lang: Lang!) {
                        ShoppingCart {
                            uuid,
                            bookings {
                                id,
                                start,
                                end,
                                price_options {
                                    id,
                                    text (lang: $lang) {
                                        checkbox
                                    }
                                },
                                room_price {
                                    id,
                                    label,
                                    verification_modal,
                                    price_options {
                                        id,
                                        text (lang: $lang) {
                                            checkbox,
                                            use_modal,
                                            modal_title,
                                            modal_content
                                        }
                                    }
                                    text (lang: $lang) {
                                        title,
                                        color,
                                        sub_title,
                                        description
                                    }
                                },
                                booking_rooms {
                                    nb_guests,
                                    id,
                                    price_plans {
                                      date,
                                      input_prices {
                                        nbGuest,
                                        edges {
                                          ...prices
                                          altPrice {
                                            ...prices
                                          }
                                        }
                                      }
                                    }
                                    room {
                                        ...roomDetails
                                    },
                                    extras {
                                        is_option,
                                        extra {
                                            ...extraDetail,
                                        },
                                        dates {
                                            date,
                                            count
                                        },
                                    }
                                }
                            }
                        }
                    }`,
                variables: {lang}
            }
        }
        return this._exec(config);
    }

    async retrieve(uuid, token, lang) {
        this.uuid = uuid;
        const config = {
            url: NetworkUrls.graphql,
            method: 'POST',
            headers: this.getHeaders({
                'X-SC': this.uuid,
                Authorization: token
            }),
            data: {
                query: `
        fragment prices on RoomPriceEdge {
            hostingPrice,
            additionalPrice,
            optionsAdditionalPrice,
            optionsBasePrice,
            extraAdditionalPrice,
            touristTax,
            totalPrice,
            from {
                id,
                label,
                verification_modal,
                price_options {
                    id,
                    text (lang: $lang) {
                        checkbox,
                        use_modal,
                        modal_title,
                        modal_content
                    }
                }
                text (lang: $lang) {
                    color,
                    left_label,
                    title,
                    sub_title,
                    description
                }
            }
        }
        query ($lang: Lang!) {
          ShoppingCart {
            uuid,
            bookings {
              id,
              start,
              end,
              room_price_id,
              room_price {
                verification_modal,
                price_options {
                    id,
                    text (lang: $lang) {
                        checkbox,
                        use_modal,
                        modal_title,
                        modal_content
                    }
                }
                text (lang: $lang) {
                  display_infos,
                  title
                }
              },
              price_plans {
                date,
                input_prices {
                    nbGuest,
                    edges {
                        ...prices
                        altPrice {
                          ...prices
                        }
                    }
                }
              }
              room {
                id,
                color,
                main_picture {
                  absolute_path
                },
                pictures (limit: 3, onlyMain: false) {
                  absolute_path
                }
                equipments {
                  icon
                },
                text (lang: $lang) {
                  name,
                  short_name,
                  description,
                  short_desc
                },
                room_group {
                  room_zone {
                    name
                  }
                },
                provider
              },
              is_free_on_date,
              extras {
                is_option,
                extra {
                  id,
                  price,
                  picture,
                  is_next_day,
                  time_to_lock,
                  type,
                  discount {
                    amount,
                    type,
                    is_available
                  },
                  text (lang: $lang) {
                    name,
                    description
                  }
                },
                dates {
                  date,
                  count
                }
              }
            }
          }
        }`,
                variables: {lang}
            }
        };
        return this._exec(config);
    }

    async createBooking({start, end, room_id, room_price_id, nb_guests, price_options}) {
        if (!this.uuid) {
            throw new Error('Missing uuid param');
        }
        const config = {
            url: NetworkUrls.graphql,
            method: 'POST',
            headers: this.getHeaders(),
            data: {
                query: `mutation ($start: Date!, $end: Date!, $room_id: CID!, $room_price_id: CID!, $nb_guests: Int!, $price_options: [CID]) {
          ShoppingCart {
            bookings {
              add(start: $start, end: $end, room_id: $room_id, room_price_id: $room_price_id, nb_guests: $nb_guests, price_options: $price_options) {
                id
              }
            }
          }
        }`,
                variables: {start, end, room_id, room_price_id, nb_guests, price_options}
            }
        };
        return this._exec(config);
    }

    async deleteBookingRoom({id}) {
        const config = {
            url: NetworkUrls.graphql,
            method: 'POST',
            headers: this.getHeaders(),
            data: {
                query: `mutation ($id: CID!) {
                  ShoppingCart {
                    bookings {
                      deleteRoom (id: $id) {
                        deleted
                      }
                    }
                  }
                }`,
                variables: {id}
            }
        };
        return this._exec(config);
    }

    async deleteBooking(variables = null) {
        if (!this.uuid) {
            throw new Error('Missing uuid param');
        }
        let config;
        if (variables.id) {
            config = {
                url: NetworkUrls.graphql,
                method: 'POST',
                headers: this.getHeaders(),
                data: {
                    query: `mutation ($id: CID!) {
          ShoppingCart {
            bookings {
              delete (id: $id) {
                deleted
              }
            }
          }
        }`,
                    variables: variables
                }
            };
        } else {
            config = {
                url: NetworkUrls.graphql,
                method: 'POST',
                headers: this.getHeaders(),
                data: {
                    query: `mutation {
            ShoppingCart {
              bookings {
                delete {
                  deleted
                }
              }
            }
          }`
                }
            };
        }

        return this._exec(config)
    }

    async addExtraToBooking({sc_booking_id, extra_id}) {
        if (!this.uuid) {
            throw new Error('Missing uuid param');
        }
        const config = {
            url: NetworkUrls.graphql,
            method: 'POST',
            headers: this.getHeaders(),
            data: {
                query: `mutation ($sc_booking_id: CID!, $extra_id: CID!) {
          ShoppingCart {
            bookings {
              extras {
                add (sc_booking_id: $sc_booking_id, extra_id: $extra_id) {
                  inserted,
                  id
                }
              }
            }
          }
        }`,
                variables: {sc_booking_id, extra_id}
            }
        }
        return this._exec(config)
    }

    async removeExtraFromBooking({sc_booking_id, extra_id}) {
        if (!this.uuid) {
            throw new Error('Missing uuid param');
        }
        const config = {
            url: NetworkUrls.graphql,
            method: 'POST',
            headers: this.getHeaders(),
            data: {
                query: `mutation ($sc_booking_id: CID!, $extra_id: CID!) {
          ShoppingCart {
            bookings {
              extras {
                delete (sc_booking_id: $sc_booking_id, extra_id: $extra_id) {
                  inserted
                }
              }
            }
          }
        }`,
                variables: {sc_booking_id, extra_id}
            }
        }
        return this._exec(config)
    }

    async addDateToExtra({sc_extra_id, date, count}) {
        if (!this.uuid) {
            throw new Error('Missing uuid param');
        }
        const config = {
            url: NetworkUrls.graphql,
            method: 'POST',
            headers: this.getHeaders(),
            data: {
                query: `mutation ($sc_extra_id: CID!, $date: Date!, $count: Int!) {
          ShoppingCart {
            bookings {
              extras {
                dates {
                  add (sc_extra_id: $sc_extra_id, date: $date, count: $count) {
                    inserted
                  }
                }
              }
            }
          }
        }`,
                variables: {sc_extra_id, date, count}
            }
        }
        return this._exec(config)
    }

    async removeDateToExtra({sc_extra_id, date}) {
        if (!this.uuid) {
            throw new Error('Missing uuid param');
        }
        const config = {
            url: NetworkUrls.graphql,
            method: 'POST',
            headers: this.getHeaders(),
            data: {
                query: `mutation ($sc_extra_id: CID!, $date: Date!) {
          ShoppingCart {
            bookings {
              extras {
                dates {
                  delete (sc_extra_id: $sc_extra_id, date: $date) {
                    deleted
                  }
                }
              }
            }
          }
        }`,
                variables: {sc_extra_id, date}
            }
        };
        return this._exec(config)
    }

    async commitBookingRoomState(data) {
        return this._exec({
            url: NetworkUrls.graphql,
            method: 'POST',
            headers: this.getHeaders(),
            data: {
                query: `mutation ($sc_booking_room_id: CID!, $extras: [ExtraInput]) {
                  ShoppingCart {
                    bookings {
                      extras {
                        setExtras (sc_booking_room_id: $sc_booking_room_id, extras: $extras) {
                          inserted
                        }
                      }
                    }
                  }
                }`,
                variables: data
            }
        })
    }

    async commitBookingState(booking) {
        const extrasToAddOrUpdate = booking.extras.map(e => ({
            id: e.extra.id,
            dates: e.dates.map(s => ({
                date: s.date,
                count: s.count
            }))
        }));
        const variables = {
            sc_booking_id: booking.id,
            extras: extrasToAddOrUpdate
        };
        const config = {
            url: NetworkUrls.graphql,
            headers: this.getHeaders(),
            method: 'POST',
            data: {
                query: `mutation ($sc_booking_id: CID!, $extras: [ExtraInput]) {
          ShoppingCart {
            bookings {
              extras {
                setExtras (sc_booking_id: $sc_booking_id, extras: $extras) {
                  inserted
                }
              }
            }
          }
        }`,
                variables: variables
            }
        };
        return this._exec(config);
    }

    async fetchBookingRoom(id, lang = 'FR') {
        const query = `fragment prices on RoomPriceEdge {
            hostingPrice,
            additionalPrice,
            optionsAdditionalPrice,
            optionsBasePrice,
            extraAdditionalPrice,
            touristTax,
            totalPrice,
            from {
                id,
                label,
                verification_modal,
                price_options {
                    id,
                    text (lang: $lang) {
                        checkbox,
                        use_modal,
                        modal_title,
                        modal_content
                    }
                }
                text (lang: $lang) {
                    color,
                    left_label,
                    title,
                    sub_title,
                    description
                }
            }
        }
        fragment roomDetails on Room {
            id,
            color,
            main_picture {
                absolute_path
            },
            pictures (limit: 3, onlyMain: false) {
                absolute_path
            }
            equipments {
                icon_dark: icon (color: "#444444")
            },
            text (lang: $lang) {
                name,
                short_name,
                description,
                short_desc
            },
            provider
        }
        fragment extraDetail on Extra {
            id,
            price,
            picture,
            is_next_day,
            time_to_lock,
            type,
            discount {
                amount,
                type,
                is_available
            },
            text (lang: $lang) {
                name,
                description
            }
        }
        query ($lang: Lang!, $id: CID!) {
            ShoppingCart {
                bookingRoom (id: $id) {
                    id,
                    nb_guests,
                    price_plans {
                        date,
                        input_prices {
                        nbGuest,
                        edges {
                            ...prices
                            altPrice {
                            ...prices
                            }
                        }
                        }
                    }
                    room {
                        ...roomDetails
                    },
                    extras {
                        is_option,
                        extra {
                            ...extraDetail,
                        },
                        dates {
                            date,
                            count
                        },
                    },
                    booking {
                        id,
                        start,
                        end,
                        price_options { id },
                        room_price {
                            id,
                            label,
                            verification_modal,
                            has_insurance,
                            price_options {
                                id,
                                text (lang: $lang) {
                                    checkbox,
                                    use_modal,
                                    modal_title,
                                    modal_content
                                }
                            }
                            text (lang: $lang) {
                                title,
                                color,
                                sub_title,
                                description
                            }
                        },
                    }
                }
            }
        }`;
        return this._exec({
            url: NetworkUrls.graphql,
            method: 'POST',
            headers: this.getHeaders(),
            data: {
                query,
                variables: {lang, id}
            }
        })
    }


    async updateBookingRoom(bookingRoomId, params) {
        const variables = {
            id: bookingRoomId,
            price_options: params.price_options
        };
        const query = `mutation ($id: CID!, $price_options: [CID], $start: Date, $end: Date, $nb_guests: Int, $room_price_id: CID) {
            ShoppingCart {
                bookings {
                    updateRoom(id: $id, price_options: $price_options, start: $start, end: $end, nb_guests: $nb_guests, room_price_id: $room_price_id) {
                        updated,
                    }
                }
            }
        }`;
        return this._exec({
            url: NetworkUrls.graphql,
            method: 'POST',
            headers: this.getHeaders(),
            data: {
                query,
                variables
            }
        })
    }

    async updateBooking({start, end, room_id, room_price_id, nb_guests, price_options, sc_booking_room_id}) {
        await this.deleteBookingRoom({id: sc_booking_room_id});
        return this.createBooking({start, end, room_id, room_price_id, nb_guests, price_options});
    }
}

export const shoppingCartService = new ShoppingCartService();



// WEBPACK FOOTER //
// ./src/network/services/shopping-cart.service.js